import styled, {css} from 'styled-components'
import get from 'lodash/get'
import breakpoint from 'components/breakpoint'
import Logo from 'components/Logo'

const getGlobeProps = props => props.theme.shell.header.languageSwitcher
const getGlobeColor = props => getGlobeProps(props).color
const getGlobeActiveColor = props => getGlobeProps(props).activeColor
const getLanguageRowColor = props => getGlobeProps(props).menu.color
const getHoverBackgroundLanguageRowColor = props => getGlobeProps(props).menu.hoverBackgroundColor
const getHoverLanguageRowColor = props => getGlobeProps(props).menu.hoverColor
const getLanguageFontSize = props => getGlobeProps(props).menu.fontSize
const getSelectedLanguageColor = props => getGlobeProps(props).menu.activeColor
const getHoverSelectedLanguageColor = props => getGlobeProps(props).menu.hoverActiveColor
const getSelectedLanguageFontFamily = props => getGlobeProps(props).menu.activeFontFamily || props.theme.font.boldFamily
const getSelectedLanguageFontWeight = props =>
  getGlobeProps(props).menu.activeFontWeight || props.theme.font.boldFontWeight
const getLanguageLineHeight = props => getGlobeProps(props).menu.lineHeight
const getBackgroundColor = props => getGlobeProps(props).backgroundColor

const GlobeButtonView = styled.button`
  display: none;
  border: none;
  padding: 12px;
  border-radius: 4px 4px 0 0;
  cursor: pointer;
  position: relative;
  background-color: ${props => (props.active ? getGlobeProps(props).backgroundColor : 'transparent')};
  box-shadow: ${props => (props.active ? '2px 10px 8px rgba(0, 0, 0, 0.2)' : 'none')};

  .globe-icon {
    path {
      fill: ${props => (props.active ? getGlobeActiveColor(props) : getGlobeColor(props))};
    }
  }

  ul {
    margin: 0;
    display: ${props => (props.active ? 'block' : 'none')};
    position: absolute;
    right: 0;
    top: 50px;
    z-index: 3;
    padding: 20px 0;
    background-color: ${getBackgroundColor};
    min-width: 150px;
    border-radius: 4px 0 4px 4px;
    box-shadow: 2px 10px 8px rgba(0, 0, 0, 0.2);

    li {
      margin-bottom: 6px;
      list-style: none;
      text-align: left;
      color: ${getLanguageRowColor};
      text-decoration: none;
      padding-left: 20px;
      height: 34px;
      font-size: ${getLanguageFontSize};
      line-height: ${getLanguageLineHeight};
      box-sizing: border-box;
      display: flex;
      align-items: center;

      &:hover {
        background-color: ${getHoverBackgroundLanguageRowColor};
        color: ${getHoverLanguageRowColor};
      }

      &:last-child {
        margin-bottom: 0;
      }

      &.selected {
        color: ${getSelectedLanguageColor};
        font-family: ${getSelectedLanguageFontFamily};
        font-weight: ${getSelectedLanguageFontWeight};

        &:hover {
          color: ${getHoverSelectedLanguageColor};
        }
      }
    }
  }

  &:hover {
    background-color: ${getBackgroundColor};
    box-shadow: 2px 10px 8px rgba(0, 0, 0, 0.2);

    .globe-icon {
      path {
        fill: ${getGlobeActiveColor};
      }
    }

    ul {
      display: block;
    }
  }

  ${breakpoint('portrait')`
      display: block;
  `};
`

const LanguageSwitcherView = styled.div`
  display: flex;
  align-items: center;
  flex: ${props => props.email && '1'};
  justify-content: flex-end;
  gap: 20px;
  right: 20px;

  @media screen and (min-width: ${props => props.theme.breakpoints.portrait}px) {
    position: unset;
    bottom: unset;
    flex: none;
  }

  @media screen and (min-width: ${props => props.theme.breakpoints.landscape}px) {
    gap: 40px;
  }
`

const LoginButtonView = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${props => props.theme.buttons.login.height};
  border: ${props => props.theme.buttons.login.border};
  padding: 8px 20px;
  color: ${props => props.theme.buttons.login.color};
  font-size: ${props => props.theme.buttons.login.fontSize};
  border-radius: 20px;
  box-sizing: border-box;
  text-decoration: none;

  &:hover {
    border: ${props => props.theme.buttons.login.hoverBorder};
    background-color: ${props => props.theme.buttons.login.hoverBackground};
    color: ${props => props.theme.buttons.login.hoverColor};
  }
`

const getBorderWidth = props => get(props.theme, 'shell.header.border.width')
const getBorderColor = props => get(props.theme, 'shell.header.border.color')
const getBorderStyle = props => get(props.theme, 'shell.header.border.style')

const getHeaderHeight = props =>
  props.email ? props.theme.shell.header.height.withEmail : props.theme.shell.header.height.default

const HeaderView = styled.div`
  position: sticky;
  transition: 0.2s;
  background-color: ${props => props.theme.shell.header.backgroundColor};
  box-shadow: ${props => props.theme.shell.header.boxShadow || 'none'};
  border-width: ${getBorderWidth};
  border-color: ${getBorderColor};
  border-style: ${getBorderStyle};
  z-index: 2;
  top: 0;
  width: 100%;
  height: ${props => (props.isScrolling ? props.theme.shell.header.height.onScroll : getHeaderHeight(props))};

  ${breakpoint('portrait')`
      height: ${props =>
        props.isScrolling ? props.theme.shell.header.height.onScroll : props.theme.shell.header.height.default};
      border-width: ${props => get(props.theme, 'shell.header.border.portrait.width')};
      border-color: ${props => get(props.theme, 'shell.header.border.portrait.color')};
      border-style: ${props => get(props.theme, 'shell.header.border.portrait.style')};
  `};
`

const getLogoHeight = heightTheme => props =>
  props.isScrolling ? props.theme.shell.header.logo.height.onScroll : get(props.theme, heightTheme)

const HeaderContainer = styled.header`
  display: flex;
  color: ${props => props.theme.shell.header.color};
  height: ${props =>
    props.isScrolling ? props.theme.shell.header.height.onScroll : props.theme.shell.header.height.default};
  padding: 0 20px;
  margin: auto;
  width: 100%;
  max-width: 1024px;
  box-sizing: border-box;
  justify-content: space-between;
  align-items: center;
  flex-direction: row;
  flex-wrap: wrap;

  ${breakpoint('portrait')`
      padding: 0 40px;
  `};
`

const centered = props =>
  !props.isProfile &&
  get(props.theme, 'shell.header.logo.centered') &&
  css`
    margin: 0 auto;
    position: relative;
  `

const BrandLogoView = styled.div`
  position: ${props => (props.isScrolling ? 'unset' : 'absolute')};
  display: flex;
  align-items: center;
  transition: 0.2s;
  height: ${getLogoHeight('shell.header.logo.height.small')};
  ${centered}

  ${breakpoint('portrait')`
    position: unset;
    height: ${getLogoHeight('shell.header.logo.height.default')};
  `};
`

const RoutesView = styled.nav`
  margin-left: 40px;
  align-self: flex-start;
  display: none;

  @media screen and (min-width: ${props => props.theme.breakpoints.portrait}px) {
    display: block;
  }
`

const RouteLinksView = styled.ul`
  display: flex;
  align-items: center;
`

const activeLinkItem = css`
  font-family: ${({theme}) => theme.font.fontBold};
  color: ${props => props.theme.shell.header.menu.hoverColor};
`

const RouteLinksItem = styled.li`
  margin-right: 20px;
  list-style: none;
  height: 46px;
  display: flex;
  align-items: end;
  border-top: 4px solid transparent;

  &:last-child {
    margin-right: 0;
  }

  a {
    display: block;
    color: ${props => props.theme.shell.header.menu.color};
    font-size: ${props => props.theme.shell.header.menu.fontSize};
    text-decoration: none;

    &:before {
      display: block;
      content: attr(title);
      font-family: ${({theme}) => theme.font.fontBold};
      font-size: ${props => props.theme.shell.header.menu.fontSize};
      height: 0;
      overflow: hidden;
      visibility: hidden;
    }

    &:hover {
      ${activeLinkItem}
    }
  }

  &.selected {
    border-top-color: ${props => props.theme.shell.header.menu.selectedBorderColor};

    a {
      ${activeLinkItem}
    }
  }
`

const PoweredView = styled.div`
  flex-shrink: 0;
  display: none;
  margin-left: 20px;

  ${breakpoint('portrait')`
    display: block;
  `};
`

const PoweredLogoLink = styled.a.attrs(() => ({
  target: '_blank',
}))`
  cursor: pointer;
`

const PoweredLogo = styled(Logo)`
  display: block;
  width: 80px;
  height: ${props => get(props.theme, 'shell.header.poweredLogo.height', '34px')};
`

const ContentView = styled.div`
  margin: 0 auto;
  max-width: 940px;
  min-width: 320px;
  width: 100%;
  box-sizing: border-box;
`

const HeaderEmailContainerView = styled.div`
  font-size: 12px;
  line-height: 20px;
  color: ${props => props.theme.brand.fontColor || '#f8f8f8'};
`

const HeaderEmailLanguageSwitchContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-bottom: 12px;
`

const HeaderEmailView = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-end;
  margin-left: 20px;

  ${breakpoint('portrait')`
    gap: 12px;
  `};
`

const HeaderEmailContentView = styled.span`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  position: ${props => (props.isScrolling ? 'unset' : 'absolute')};
  left: 0;
  bottom: 0;
  font-size: ${props => (props.isScrolling ? '14px' : '12px')};
  line-height: ${props => (props.isScrolling ? '21px' : '18px')};
  width: ${props => (props.isScrolling ? 'unset' : '100vw')};
  padding: ${props => (props.isScrolling ? '0' : '0 20px 16px')};
  max-width: ${props => (props.isScrolling ? 'calc((100vw / 2) - 68px)' : 'calc(100vw - 40px)')};

  ${breakpoint('portrait')`
    position: unset;
    font-size: 14px;
    line-height: 21px;
    width: unset;
    padding: 0;
  `};
`

const HeaderEmailSeparator = styled.span`
  display: none;
  height: 20px;
  border-right: 1px solid ${props => props.theme.shell.header.color};

  ${breakpoint('portrait')`
    display: block;
  `};
`

const HeaderLogout = styled.button`
  display: none;
  color: inherit;
  cursor: pointer;
  text-decoration: underline;
  background-color: transparent;
  border: none;
  margin: 0;
  padding: 0;
  font-size: 14px;
  line-height: 21px;

  &:hover,
  &:focus {
    text-decoration: none;
  }

  ${breakpoint('portrait')`
    display: inline-block;
  `};
`

export {
  ContentView,
  GlobeButtonView,
  LanguageSwitcherView,
  LoginButtonView,
  HeaderView,
  BrandLogoView,
  RoutesView,
  RouteLinksView,
  RouteLinksItem,
  HeaderContainer,
  PoweredView,
  PoweredLogoLink,
  PoweredLogo,
  HeaderEmailContainerView,
  HeaderEmailLanguageSwitchContainer,
  HeaderEmailView,
  HeaderEmailContentView,
  HeaderEmailSeparator,
  HeaderLogout,
}
